import { clsx } from 'clsx'
import { LinearGradient } from 'expo-linear-gradient'
import { useRouter } from 'expo-router'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Card, CardBody } from '@/src/elements/card'
import { Image } from '@/src/elements/image'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import type { TMediumCommunityApi } from '@/src/features/community/api/communityApi.types'
import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import type { TComputedSubscription } from '@/src/features/community/utils/getComputedSubscription'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useRoutes } from '@/src/general/utils/routes'

import { CommunityProgressBar, type TCommunityProgressBarProps } from '../../CommunityProgressBar'
import { CommunityStatusSection, type TCommunityStatusSectionProps } from '../../CommunityStatusSection'

export type TJourneyThumbnailProps = Omit<TCommunityStatusSectionProps, 'community' | 'short' | 'subscription'> & {
  community: Pick<TMediumCommunityApi, 'id' | 'slug' | 'name' | 'picture'> & TCommunityStatusSectionProps['community']
  subscription: Pick<TComputedSubscription, 'isPrayedToday'> &
    TCommunityProgressBarProps &
    TCommunityStatusSectionProps['subscription']
  className?: string
}

export const JourneyThumbnail = ({ className, community, subscription, ...otherProps }: TJourneyThumbnailProps) => {
  const { routes } = useRoutes()
  const { navigate } = useRouter()

  return (
    <Card
      testID={TEST_ID.PRAYER_SPACE.JOURNEY_SLIDER_THUMBNAIL}
      additionalProps={{ role: 'link' }}
      isPressable
      onPress={() =>
        navigate(routes.community({ id: community.id, slug: community.slug, origin: EVENT_ORIGIN.UNDEFINED }))
      }
      shadow="md"
      className={clsx('h-[280px] bg-white justify-end', className)}
    >
      <Image source={getImageUrl(community.picture)} className="absolute" contentFit="cover" transition={null} />
      {subscription.isPrayedToday && (
        <>
          <View className="absolute top-0 left-0 right-0 bottom-0 opacity-50 bg-white" />

          <View className="absolute top-4 left-4 bg-success-bg-light rounded-md p-2 pr-4 flex-row items-center gap-2">
            <View className="bg-success-text-contrast-low rounded-full p-1">
              <Icon name={ICON.CHECK} size="xs" className="text-white" />
            </View>
            <Typography bold colors="success" size="xs">
              <FormattedMessage id="journey.prayed.today" />
            </Typography>
          </View>
        </>
      )}
      <CardBody className="gap-4 p-4 grow-0 pt-10">
        <LinearGradient
          className="absolute top-0 bottom-0 left-0 right-0 opacity-80"
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          colors={['transparent', 'black']}
        />
        <View>
          <CommunityStatusSection community={community} subscription={subscription} {...otherProps} />
          <Typography numberOfLines={2} black size="lg" colors="white" className="mt-3">
            {community.name}
          </Typography>
        </View>
        <CommunityProgressBar progress={subscription.progress} />
      </CardBody>
    </Card>
  )
}
